import React, { useMemo } from 'react';
import classNames from 'classnames';
import ArrowupIcon from 'icons/Arrowup.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import { FilterOption } from 'models/Filters';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { EMPTY_ARRAY, SORT_PARAM_KEYWORD } from 'data/global';
import useActiveFilterSortPills from './useActiveFilterSortPills';
import ClearFilters from 'components/ClearFilters';
import { useTranslation } from 'react-i18next';
import ActiveFilterSortPill from './components/ActiveFilterSortPill';

import './ActiveFilterSortPills.styles.scss';

type ActiveFilterSortPillsProps = {
  className?: string;
  filterOptions: FilterOption[];
  sortOptions: OptionValue<string>[];
  sortParam?: string;
  sortClearFields?: string[];
  onChipLabelClick?: (fieldKey: string) => void;
  onSortChipLabelClick?: (fieldKey: string) => void;
};

const ActiveFilterSortPills: React.FC<ActiveFilterSortPillsProps> = (props) => {
  const {
    className,
    filterOptions,
    sortOptions,
    sortParam = SORT_PARAM_KEYWORD,
    sortClearFields = EMPTY_ARRAY,
    onChipLabelClick,
    onSortChipLabelClick,
  } = props;

  const { filterPills, sortPills, hasPills } = useActiveFilterSortPills(
    filterOptions,
    sortOptions,
    sortParam,
    sortClearFields,
    onChipLabelClick,
  );

  const classes = classNames('active-filter-sort-pills', className);

  const { t } = useTranslation();

  const filterKeys = useMemo(
    () => [
      ...filterOptions.flatMap(({ value, clearFields = [] }) => [
        value,
        ...clearFields,
      ]),
      sortParam,
      ...sortClearFields,
    ],
    [filterOptions, sortParam, sortClearFields],
  );

  if (!hasPills) return null;

  return (
    <div className={classes}>
      {sortPills.map(({ key, field, label, isDesc, onRemove }) => (
        <ActiveFilterSortPill
          key={key}
          label={label}
          onRemove={onRemove}
          {...(onSortChipLabelClick && {
            onLabelClick: () => onSortChipLabelClick(field),
          })}
        >
          {isDesc ? <ArrowdownIcon /> : <ArrowupIcon />}
        </ActiveFilterSortPill>
      ))}

      {filterPills.map(
        ({ key, displayValue, label, onRemove, onLabelClick }) => (
          <ActiveFilterSortPill
            key={key}
            label={displayValue ? `${label}: ${displayValue}` : label}
            onRemove={onRemove}
            {...(onLabelClick && {
              onLabelClick: () => onLabelClick(key),
            })}
          />
        ),
      )}

      <ClearFilters filters={filterKeys} text={t('clearAll')} />
    </div>
  );
};

export default ActiveFilterSortPills;
